import React, { DetailedHTMLProps, HTMLAttributes, useCallback, useEffect, useMemo } from 'react'
import { CSSTransition } from 'react-transition-group'
import {
  arrow as arrowModifier,
  flip as flipModifier,
  offset as offsetModifier,
  Options,
  preventOverflow as preventOverflowModifier,
} from '@popperjs/core'
import { Breakpoint, useBreakpointEnding, ZLayers } from '~theme'
import { usePopper, useMinZIndex } from '~hooks'
import { Button } from '~components/Button'
import { Icon, IconName } from '~components/Icon'
import { Portal } from '~components/Portal'
import { TooltipProps } from './Tooltip.types'
import { viewModalOnMobile } from './Tooltip.modifiers'
import { getFallbackPlacement } from './Tooltip.utils'
import * as Styled from './Tooltip.styles'

const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>((props, ref) => {
  const {
    strategy = 'fixed',
    position = 'top',
    on = 'hover',
    propagateHoverToContent = false,
    propagateHoverOnMobile = false,
    hasMobileView = true,
    hasArrow = true,
    offset = 4,
    offsetSkidding = 0,
    duration = 150,
    isClosable = false,
    enableFlip = false,
    enablePreventOverflow = true,
    fallbackPlacements,
    innerWidth,

    content,
    children,
    icon,
    link,
    targetRef,

    isOpenForce,
    onOpen,
    onClose,
    ...otherProps
  } = props

  const { zIndex, ZIndexContextProvider } = useMinZIndex(ZLayers.tooltip)

  const isMobile = useBreakpointEnding(Breakpoint.md)

  const config: Options = useMemo(
    () => ({
      placement: position,
      strategy,
      modifiers: [
        offsetModifier,
        arrowModifier,
        flipModifier,
        preventOverflowModifier,
        viewModalOnMobile,
        {
          name: 'offset',
          options: {
            offset: [offsetSkidding, offset],
          },
        },
        {
          name: 'arrow',
          options: {
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: enablePreventOverflow,
          options: {
            padding: 8,
          },
        },
        {
          name: 'flip',
          enabled: enableFlip,
          options: {
            padding: offset,
            fallbackPlacements: fallbackPlacements || getFallbackPlacement(position),
          },
        },
        {
          name: 'viewModalOnMobile',
          enabled: hasMobileView,
        },
      ],
    }),
    [enableFlip, enablePreventOverflow, fallbackPlacements, hasMobileView, offset, offsetSkidding, position, strategy],
  )

  const { triggerRef, popupRef, innerRef, isOpen, showHandler, hideHandler, create, destroy } = usePopper(
    config,
    [children, innerWidth],
    targetRef,
    isOpenForce,
  )

  const onEnter = useCallback(() => {
    create()
    onOpen?.()
  }, [create, onOpen])

  useEffect(() => {
    // Show tooltip forcibly with slight timeout, so its appearance will be noticeable
    setTimeout(() => {
      if (isOpenForce && targetRef?.current) {
        showHandler()
      }
    }, 500)
  }, [isOpenForce, showHandler, targetRef])

  const propagateHoverToContentProps = useMemo(() => {
    if (!propagateHoverToContent || on !== 'hover' || isMobile) {
      return {
        hoverOffset: false,
      }
    }

    return {
      hoverOffset: true,
      onMouseEnter: showHandler,
      onMouseLeave: hideHandler,
    }
  }, [isMobile, on, propagateHoverToContent, showHandler, hideHandler])

  const triggerElement = useMemo(() => {
    const triggerProps: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> = {}

    if (!targetRef) {
      triggerProps.ref = triggerRef
    }

    if (on === 'click' || (propagateHoverOnMobile && isMobile)) {
      triggerProps.onClick = isOpen ? hideHandler : showHandler
    }

    if (on === 'hover' && !isMobile) {
      triggerProps.onMouseEnter = showHandler
      triggerProps.onMouseLeave = hideHandler
    }

    return children ? React.cloneElement(children, triggerProps) : children
  }, [children, hideHandler, isMobile, isOpen, on, propagateHoverOnMobile, showHandler, targetRef, triggerRef])

  return (
    <>
      {triggerElement}

      <Portal rootId="tooltip-root" rootRef={targetRef}>
        {content && (
          <CSSTransition
            nodeRef={popupRef}
            unmountOnExit={true}
            in={isOpen}
            appear={isOpenForce}
            timeout={duration}
            onEnter={onEnter}
            onExited={destroy}
            onExit={onClose}
            classNames="tooltip">
            <Styled.Container
              ref={popupRef}
              duration={duration}
              role="tooltip"
              hasMobileView={hasMobileView}
              zIndex={zIndex}
              $offset={offset}
              {...propagateHoverToContentProps}
              {...otherProps}>
              <ZIndexContextProvider>
                <Styled.Wrapper ref={innerRef} duration={duration} innerWidth={innerWidth}>
                  <Styled.Inner ref={ref}>
                    {icon && (
                      <Styled.IconWrapper>
                        <Icon name={icon} />
                      </Styled.IconWrapper>
                    )}

                    {typeof content === 'function' ? content(hideHandler) : content}

                    {link && <Styled.LinkWrapper>{link}</Styled.LinkWrapper>}

                    {isClosable && (
                      <Styled.ButtonWrapper>
                        <Button color="base" variant="transparent" size="small" shape="circle" onClick={hideHandler}>
                          <Icon name={IconName.times} />
                        </Button>
                      </Styled.ButtonWrapper>
                    )}
                  </Styled.Inner>
                  <Styled.Arrow isVisible={hasArrow} data-popper-arrow />
                </Styled.Wrapper>
              </ZIndexContextProvider>
            </Styled.Container>
          </CSSTransition>
        )}
      </Portal>
    </>
  )
})

Tooltip.displayName = 'Tooltip'
export default Tooltip
